<template>
  <div class="ad" @click.stop>
    <button ref="btnRef" class="drop-btn" :class="{ open: isOpen }" @click="toggle">
      <span class="pkg-icon">
        <i :class="'fa fa-' + icon"></i>
      </span>
      <span>{{ label }}</span>
      <span class="count-badge" :class="{ empty: !natures?.length }">
        {{ natures?.length ?? 0 }}
      </span>
      <i
        class="fa fa-chevron-down"
        :style="{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }"
      ></i>
    </button>

    <Teleport to="body">
      <div v-if="isOpen" class="drop-menu open" :style="menuStyle" @click.stop>
        <template v-if="natures?.length">
          <div class="drop-header">
            <span class="drop-title">{{ headerTitle || ('Assigned ' + label) }}</span>
            <span class="drop-count">{{ natures.length }}</span>
          </div>

          <div class="asset-list">
            <div class="asset-item" v-for="(nature, index) in visibleNatures" :key="index">
              <div class="asset-icon">
                <i :class="'fa fa-' + rowIcon"></i>
              </div>
              <div class="asset-info">
                <span class="asset-name">{{ nature }}</span>
              </div>
            </div>
          </div>

          <div v-if="hiddenCount > 0" class="more-pill">
            <i class="fa fa-ellipsis-h"></i>
            {{ hiddenCount }} more {{ label.toLowerCase() }}
          </div>
        </template>

        <div v-else class="empty-state">
          <div class="empty-icon"><i class="fa fa-inbox"></i></div>
          <span class="empty-text">No {{ label.toLowerCase() }} assigned</span>
          <span class="empty-sub">No linked {{ label.toLowerCase() }} for this record</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
let _uid = 0

export default {
  name: 'NatureDropdown',

  props: {
    natures:    { type: Array, default: () => [] },
    label:      { type: String, default: 'Nature' },
    headerTitle:{ type: String, default: '' },
    icon:       { type: String, default: 'leaf' },
    rowIcon:    { type: String, default: 'leaf' },
    maxVisible: { type: Number, default: 3 }
  },

  data() {
    return {
      uid: ++_uid,
      isOpen: false,
      menuStyle: {}
    }
  },

  computed: {
    visibleNatures() {
      return this.natures?.slice(0, this.maxVisible) ?? []
    },
    hiddenCount() {
      return Math.max(0, (this.natures?.length ?? 0) - this.maxVisible)
    }
  },

  mounted() {
    document.addEventListener('click', this.closeOnOutside)
    document.addEventListener('items-dropdown:open', this.onSiblingOpen)
    window.addEventListener('scroll', this.updatePosition, true)
    window.addEventListener('resize', this.updatePosition)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closeOnOutside)
    document.removeEventListener('items-dropdown:open', this.onSiblingOpen)
    window.removeEventListener('scroll', this.updatePosition, true)
    window.removeEventListener('resize', this.updatePosition)
  },

  methods: {
    toggle() {
      if (this.isOpen) {
        this.isOpen = false
        return
      }
      document.dispatchEvent(new CustomEvent('items-dropdown:open', { detail: { uid: this.uid } }))
      this.isOpen = true
      this.$nextTick(this.updatePosition)
    },
    onSiblingOpen(e) {
      if (e.detail.uid !== this.uid) this.isOpen = false
    },
    closeOnOutside() {
      this.isOpen = false
    },
    updatePosition() {
      if (!this.$refs.btnRef) return
      const rect = this.$refs.btnRef.getBoundingClientRect()
      const menuWidth = 240
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const openUpward = spaceBelow < 280 && spaceAbove > spaceBelow

      let left = rect.left
      if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8

      this.menuStyle = {
        position: 'fixed',
        zIndex: 9999,
        minWidth: menuWidth + 'px',
        left: left + 'px',
        ...(openUpward
          ? { bottom: (window.innerHeight - rect.top + 8) + 'px', top: 'auto' }
          : { top: (rect.bottom + 8) + 'px', bottom: 'auto' })
      }
    }
  }
}
</script>

<style scoped>
/* Reuse dropdown styling from ItemsDropdown */
.ad {
  position: relative;
  display: inline-block;
}

.drop-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px 6px 10px;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  transition: background 0.12s, border-color 0.12s, box-shadow 0.12s;
  white-space: nowrap;
}
.drop-btn:hover {
  background: #f9fafb;
  border-color: #b3b9c4;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1);
}
.drop-btn.open {
  background: #f9fafb;
  border-color: #7f77dd;
  box-shadow: 0 0 0 3px #eeedfe;
}
.drop-btn .pkg-icon {
  width: 16px;
  height: 16px;
  border-radius: 6px;
  background: #eeedfe;
  display: flex;
  align-items: center;
  justify-content: center;
}
.drop-btn .pkg-icon .fa { font-size: 11px; color: #534ab7; }
.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 17px;
  padding: 0 5px;
  background: #eeedfe;
  color: #3c3489;
  border: 1px solid #afa9ec;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}
.count-badge.empty {
  background: #f2f4f7;
  color: #667085;
  border-color: #d0d5dd;
}
.drop-menu {
  background: #f1f8ff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.1), 0 2px 6px rgba(16, 24, 40, 0.06);
}
.drop-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px;
  border-bottom: 1px solid #f2f4f7;
  background: #f9fafb;
}
.drop-header .drop-title {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.drop-header .drop-count {
  font-size: 11px;
  font-weight: 600;
  color: #3c3489;
  background: #eeedfe;
  padding: 2px 6px;
  border-radius: 999px;
}
.asset-list {
  padding: 10px 12px;
}
.asset-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid #eff3f9;
}
.asset-item:last-child {
  border-bottom: none;
}
.asset-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.asset-icon .fa { color: #5b56d8; font-size: 12px; }
.asset-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #101828;
  text-transform: capitalize;
}
.more-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 10px 12px;
  padding: 6px 10px;
  background: #eef2ff;
  border-radius: 999px;
  color: #4545a4;
  font-size: 12px;
  font-weight: 600;
}
.empty-state {
  padding: 20px;
  text-align: center;
}
.empty-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: #e9efff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty-icon .fa { color: #4f46e5; }
.empty-text {
  display: block;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.empty-sub {
  display: block;
  font-size: 12px;
  color: #667085;
}
</style>
